
import { toggleAudioOnly, toggleCarModeAudioOnly } from '../base/audio-only/actions';
import { getCurrentConference } from '../base/conference';
import { MEDIA_TYPE, setVideoMuted } from '../base/media';
import { VIDEO_MUTISM_AUTHORITY } from '../base/media/constants';
import { PIN_PARTICIPANT, SET_MODERATOR_PIN, getPinnedParticipant } from '../base/participants';
import { MiddlewareRegistry, StateListenerRegistry } from '../base/redux';
import { getLocalVideoType } from '../base/tracks';
import { SET_DOCUMENT_EDITING_STATUS } from '../etherpad';

import { SET_CAR_MODE, SET_TILE_VIEW } from './actionTypes';
import { setTileView } from './actions';
import { LAYOUTS } from './constants';
import { isLayoutAllowed } from './functions';
import { getActivePresentationItems } from './presentationStack';

import './subscriber';

let previousTileViewEnabled;

/**
 * Middleware which intercepts actions and updates tile view related state.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    const result = next(action);

    switch (action.type) {

    case SET_CAR_MODE:
        {
            const mediaType = getLocalVideoType(store.getState()['features/base/tracks']);
            const { enabled: audioOnly } = store.getState()['features/base/audio-only'];

            store.dispatch(setVideoMuted(action.enabled, mediaType, VIDEO_MUTISM_AUTHORITY.CAR_MODE, true));

            if (action.enabled && !audioOnly) {
                store.dispatch(toggleAudioOnly());
                store.dispatch(toggleCarModeAudioOnly());
            }
        }

        if (!action.enabled) {
            store.dispatch(setVideoMuted(true));
            store.dispatch(toggleAudioOnly());
            store.dispatch(toggleCarModeAudioOnly());
        }

        break;

    // Actions that temporarily clear the user preferred state of tile view,
    // then re-set it when needed.
    case PIN_PARTICIPANT: {
        const pinnedParticipant = getPinnedParticipant(store.getState());

        if (pinnedParticipant) {
            _storeTileViewStateAndClear(store);
        } else {
            _restoreTileViewState(store);
        }
        break;
    }
    case SET_MODERATOR_PIN: {
        const { action: pinAction = 'pin' } = action.participant || {};

        if (pinAction === 'pin') {
            _storeTileViewStateAndClear(store);
        } else {
            const hasPinned = Boolean(getPinnedParticipant(store.getState()));

            if (!hasPinned) {
                _restoreTileViewState(store);
            }
        }
        break;
    }
    case SET_DOCUMENT_EDITING_STATUS:
        if (action.editing) {
            _storeTileViewStateAndClear(store);
        } else {
            _restoreTileViewState(store);
        }
        break;

    // Things to update when tile view state changes
    case SET_TILE_VIEW:
        if (action.enabled && getPinnedParticipant(store)) {
            // store.dispatch(pinParticipant(null, true));
        }
    }

    return result;
});

/**
 * Set up state change listener to perform maintenance tasks when the conference
 * is left or failed.
 */
StateListenerRegistry.register(
    state => getCurrentConference(state),
    (conference, { dispatch }, previousConference) => {
        if (conference !== previousConference) {
            // conference changed, left or failed...
            // Clear tile view state.
            dispatch(setTileView());
        }
    });

/**
 * Checks whether any presentation or screen share is currently active.
 *
 * @param {Object} state - The Redux state.
 * @returns {boolean}
 */
function _isPresentationActive(state) {
    const activeItems = getActivePresentationItems(state);

    if (activeItems && activeItems.length > 0) {
        return true;
    }

    const tracks = state['features/base/tracks'] || [];

    return tracks.some(t => t.mediaType === MEDIA_TYPE.SCREENSHARE && !t.muted);
}

/**
 * Respores tile view state, if it wasn't updated since then.
 *
 * @param {Object} store - The Redux Store.
 * @returns {void}
 */
function _restoreTileViewState({ dispatch, getState }) {
    const state = getState();

    // Do not restore tile view while a presentation or screen share is currently active.
    if (_isPresentationActive(state)) {
        return;
    }

    if (previousTileViewEnabled !== undefined) {
        dispatch(setTileView(previousTileViewEnabled));
    }

    previousTileViewEnabled = undefined;
}

/**
 * Stores the current tile view state and clears it.
 *
 * @param {Object} store - The Redux Store.
 * @returns {void}
 */
function _storeTileViewStateAndClear({ dispatch, getState }) {
    const state = getState();
    const nonTileAllowed = isLayoutAllowed(state, LAYOUTS.VERTICAL_FILMSTRIP_VIEW)
        || isLayoutAllowed(state, LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW)
        || isLayoutAllowed(state, LAYOUTS.STAGE_VIEW);

    if (!nonTileAllowed) {
        return;
    }

    const { tileViewEnabled } = state['features/video-layout'];

    if (tileViewEnabled !== undefined) {
        if (previousTileViewEnabled === undefined) {
            previousTileViewEnabled = tileViewEnabled;
        }
        dispatch(setTileView(false));
    }
}
